import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import { Can } from "../../components/Can";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContactModal from "../../components/ContactModal";
import PermissionModal from "../../components/PermissionModal";
import ContactsImportModal from "../../components/ContactsImportModal";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import openSocket from "../../services/socket-io";
import { i18n } from "../../translate/i18n";
import ExportingModal from "./ExportingModal";

const CHANNEL_ICONS = {
	whatsapp: "https://img.icons8.com/color/48/000000/whatsapp.png",
	telegram: "https://img.icons8.com/color/48/000000/telegram-app--v1.png",
	instagram: "https://img.icons8.com/fluency/48/000000/instagram-new.png",
	facebook: "https://img.icons8.com/color/48/000000/facebook-new.png",
	whatsappbusiness:
		"https://e7.pngegg.com/pngimages/362/594/png-clipart-whatsapp-inc-business-whatsapp-text-logo.png",
};

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

const PAGE_SIZE = 20;
const MAX_VISIBLE_TAGS = 2;

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconPlus({ className = "size-4 me-1 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconDownload({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="7 10 12 15 17 10" />
			<line x1="12" y1="15" x2="12" y2="3" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconUpload({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="17 8 12 3 7 8" />
			<line x1="12" y1="3" x2="12" y2="15" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconChat({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconBan() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<path d="m4.9 4.9 14.2 14.2" />
		</svg>
	);
}

function ContactTagBadge({ tag }) {
	const hex = tag.color || "#64748b";
	return (
		<span
			className="inline-flex max-w-[6.5rem] shrink-0 items-center truncate rounded px-2.5 py-0.5 text-xs font-medium text-white"
			style={{ backgroundColor: hex }}
			title={tag.name}
		>
			{tag.name}
		</span>
	);
}

function ContactTagsCell({ tags = [] }) {
	if (!tags.length) {
		return <span className="text-default-500">—</span>;
	}

	const visible = tags.slice(0, MAX_VISIBLE_TAGS);
	const extra = tags.length - MAX_VISIBLE_TAGS;
	const allNames = tags.map((t) => t.name).join(", ");

	return (
		<div
			className="flex max-w-[220px] items-center gap-1 overflow-hidden"
			title={allNames}
		>
			{visible.map((tag) => (
				<ContactTagBadge key={tag.id} tag={tag} />
			))}
			{extra > 0 && (
				<span
					className="inline-flex shrink-0 items-center rounded px-2 py-0.5 text-xs font-medium bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300"
					title={allNames}
				>
					+{extra}
				</span>
			)}
		</div>
	);
}

function ContactAvatar({ contact }) {
	const channelIcon = CHANNEL_ICONS[contact.channel];

	return (
		<div className="relative shrink-0">
			<div className="size-10 overflow-hidden rounded-full bg-default-200 dark:bg-default-700">
				{contact.profilePicUrl ? (
					<img
						src={contact.profilePicUrl}
						alt=""
						className="size-10 rounded-full object-cover"
					/>
				) : (
					<div className="flex size-10 items-center justify-center text-sm font-semibold text-default-600 dark:text-default-300">
						{(contact.name || "?").charAt(0).toUpperCase()}
					</div>
				)}
			</div>
			{channelIcon && (
				<span className="absolute -bottom-0.5 -end-0.5 flex size-[22px] items-center justify-center overflow-hidden rounded-full border-2 border-card bg-card dark:border-default-800">
					<img src={channelIcon} alt="" className="size-[18px] object-contain" />
				</span>
			)}
		</div>
	);
}

function ContactRowActions({ onChat, onEdit, onDelete, canDelete, onToggleBlock, isBlocked }) {
	return (
		<div className="flex items-center gap-1">
			<button
				type="button"
				className="btn size-7.5 bg-success/10 text-success hover:bg-success/20 dark:bg-success/15 dark:hover:bg-success/25"
				title={i18n.t("contacts.buttons.chat")}
				aria-label={i18n.t("contacts.buttons.chat")}
				onClick={onChat}
			>
				<IconChat />
			</button>
			<ContactRowMenu
				onEdit={onEdit}
				onDelete={onDelete}
				canDelete={canDelete}
				onToggleBlock={onToggleBlock}
				isBlocked={isBlocked}
			/>
		</div>
	);
}

function ContactRowMenu({ onEdit, onDelete, canDelete, onToggleBlock, isBlocked }) {
	return (
		<AppTableActionMenu
			triggerIcon={<IconMore />}
			triggerAriaLabel={i18n.t("contacts.table.actions")}
			items={[
				{
					key: "edit",
					label: i18n.t("contacts.buttons.edit"),
					icon: <IconEdit />,
					onClick: onEdit,
				},
				onToggleBlock && {
					key: "block",
					label: isBlocked ? "Desbloquear contato" : "Bloquear contato",
					icon: <IconBan />,
					tone: isBlocked ? undefined : "danger",
					onClick: onToggleBlock,
				},
				canDelete && {
					key: "delete",
					label: i18n.t("contacts.buttons.delete"),
					icon: <IconDelete />,
					tone: "danger",
					onClick: onDelete,
				},
			]}
		/>
	);
}

function ContactsTableSkeleton() {
	return (
		<>
			{[1, 2, 3, 4, 5, 6].map((row) => (
				<tr key={row} className="animate-pulse">
					<td className="py-3 ps-4">
						<div className="size-4 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="flex items-center gap-3">
							<div className="size-10 rounded-full bg-default-200 dark:bg-default-700" />
							<div className="h-4 w-32 rounded bg-default-200 dark:bg-default-700" />
						</div>
					</td>
					<td className="px-3.5 py-3">
						<div className="h-5 w-24 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-28 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-20 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-36 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="flex gap-1">
							<div className="size-7.5 rounded bg-default-200 dark:bg-default-700" />
							<div className="size-7.5 rounded bg-default-200 dark:bg-default-700" />
						</div>
					</td>
				</tr>
			))}
		</>
	);
}

const reducer = (state, action) => {
	if (action.type === "SET_CONTACTS") {
		return action.payload;
	}

	if (action.type === "UPDATE_CONTACTS") {
		const contact = action.payload;
		const contactIndex = state.findIndex((c) => c.id === contact.id);

		if (contactIndex !== -1) {
			state[contactIndex] = contact;
			return [...state];
		}
		return [contact, ...state];
	}

	if (action.type === "DELETE_CONTACT") {
		const contactId = action.payload;

		const contactIndex = state.findIndex((c) => c.id === contactId);
		if (contactIndex !== -1) {
			state.splice(contactIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

function Contacts() {
	const history = useHistory();
	const location = useLocation();

	const { user } = useContext(AuthContext);

	const [exportCount, setExportCount] = useState(0);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParam, setSearchParam] = useState("");
	const [contacts, dispatch] = useReducer(reducer, []);
	const [selectedContactId, setSelectedContactId] = useState(null);
	const [selectedContact, setSelectedContact] = useState(null);
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [deletingContact, setDeletingContact] = useState(null);
	const [deletingSelectedIds, setDeletingSelectedIds] = useState(null);
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const [importContactsModal, setImportContactsModal] = useState(false);
	const [selectedTags, setSelectedTags] = useState([]);
	const [onlyBlocked, setOnlyBlocked] = useState(false);
	const [blockingContact, setBlockingContact] = useState(null);

	useEffect(() => {
		const term = location.state?.topbarSearch;
		if (!term) return;
		setSearchParam(term);
		history.replace({ pathname: location.pathname, search: location.search });
	}, [location.state, history, location.pathname, location.search]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setSelectedIds(new Set());
	}, [searchParam, selectedTags, onlyBlocked]);

	const visibleIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
	const allVisibleSelected =
		visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
	const someVisibleSelected =
		visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;
	const selectedCount = selectedIds.size;

	const selectAllRef = useRef(null);
	useEffect(() => {
		if (!selectAllRef.current) return;
		selectAllRef.current.indeterminate = someVisibleSelected;
		selectAllRef.current.checked = allVisibleSelected;
	}, [someVisibleSelected, allVisibleSelected]);

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allVisibleSelected) {
				visibleIds.forEach((id) => next.delete(id));
			} else {
				visibleIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const toggleSelectOne = (id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const rangeStart = total ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, total);

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts/", {
						params: {
							searchParam,
							pageNumber,
							tags: selectedTags,
							isBlocked: onlyBlocked || undefined,
						},
					});

					const count = data.count ?? 0;
					const lastPage = Math.max(1, Math.ceil(count / PAGE_SIZE));

					if (pageNumber > lastPage) {
						setPageNumber(lastPage);
						return;
					}

					dispatch({ type: "SET_CONTACTS", payload: data.contacts || [] });
					setTotal(count);
					setHasMore(Boolean(data.hasMore));
				} catch (err) {
					toastError(err);
				} finally {
					setLoading(false);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber, selectedTags, onlyBlocked]);

	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket({
			scope: "contacts",
			userId: user.id,
			component: "Contacts",
			companyId: user?.companyId ?? "",
		});
		if (!socket) return undefined;

		socket.on("contact", (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user?.id, user?.companyId]);

	const handleSearch = (event) => {
		setSearchParam(event.target.value.toLowerCase());
	};

	const handleOpenContactModal = () => {
		setSelectedContactId(null);
		setContactModalOpen(true);
	};

	const handleCloseContactModal = () => {
		setSelectedContactId(null);
		setContactModalOpen(false);
	};

	const handleGoToConversation = async (contact) => {
		if (!contact?.id) return;

		try {
			const search = (contact.number || contact.name || "").trim();
			if (search) {
				for (const status of ["open", "pending"]) {
					const { data } = await api.get("/tickets", {
						params: {
							searchParam: search,
							pageNumber: 1,
							status,
							showAll: true,
						},
					});
					const existingTicket = data.tickets?.find(
						(t) => Number(t.contactId) === Number(contact.id)
					);
					if (existingTicket?.id) {
						history.push(`/tickets/${existingTicket.id}`);
						return;
					}
				}
			}
		} catch (err) {
			toastError(err);
		}

		setSelectedContact(contact);
		setNewTicketModalOpen(true);
	};

	const hadleEditContact = (contactId) => {
		setSelectedContactId(contactId);
		setContactModalOpen(true);
	};

	const handleDeleteContact = async (contactId) => {
		try {
			await api.delete(`/contacts/${contactId}`);
			toast.success(i18n.t("contacts.toasts.deleted"));
			dispatch({ type: "DELETE_CONTACT", payload: contactId });
		} catch (err) {
			toastError(err);
		}
		setDeletingContact(null);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.delete(contactId);
			return next;
		});
		if (contacts.length === 1 && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		}
	};

	const handleDeleteSelected = async (ids) => {
		try {
			await Promise.all(ids.map((id) => api.delete(`/contacts/${id}`)));
			toast.success(i18n.t("contacts.toasts.deletedSelected"));
			ids.forEach((id) => dispatch({ type: "DELETE_CONTACT", payload: id }));
		} catch (err) {
			toastError(err);
		}
		setDeletingSelectedIds(null);
		setSelectedIds(new Set());
		if (contacts.length <= ids.length && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		}
	};

	const confirmDeleteSelected = () => {
		if (selectedCount === 0) return;
		if (user.profile !== "admin") {
			setPermissionOpen(true);
			return;
		}
		setDeletingContact(null);
		setDeletingSelectedIds([...selectedIds]);
		setConfirmOpen(true);
	};

	const confirmDeleteOne = (contact) => {
		if (user.profile !== "admin") {
			setPermissionOpen(true);
			return;
		}
		setDeletingContact(contact);
		setDeletingSelectedIds(null);
		setConfirmOpen(true);
	};

	const handleToggleBlock = async (contact) => {
		const next = !contact.isBlocked;
		try {
			const { data } = await apiV2.put(`/contacts/${contact.id}/block`, { blocked: next });
			const isBlocked = Boolean(data?.isBlocked);
			if (onlyBlocked && !isBlocked) {
				dispatch({ type: "DELETE_CONTACT", payload: contact.id });
				setTotal((t) => Math.max(0, t - 1));
			} else {
				dispatch({ type: "UPDATE_CONTACTS", payload: { ...contact, isBlocked } });
			}
			toast.success(isBlocked ? "Contato bloqueado." : "Contato desbloqueado.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleSelectedTags = (selecteds) => {
		const tags = selecteds.map((t) => t.id);
		setSelectedTags(tags);
	};

	const exportContacts = async () => {
		setExportLoading(true);
		setExportModalOpen(true);

		try {
			const {
				data: { contacts: exportList, count },
			} = await api.get("/contacts-export");

			const csv = exportList.map((contact) => {
				if (contact.name) {
					let row = "";
					row += `${contact.name},${contact.number},${contact.email || ""}`;

					if (contact.tags.length > 0) {
						contact.tags.forEach((tag) => {
							row += `,${tag.name}`;
						});
					}

					return row;
				}
				return null;
			});

			const csvString = `name,number,email\n${csv.filter(Boolean).join("\n")}`;

			const blob = new Blob([csvString], { type: "text/csv" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.setAttribute(
				"download",
				`contatos-${new Date().toLocaleDateString("pt-BR")}.csv`
			);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setExportCount(count);
		} catch (err) {
			toastError(err);
		} finally {
			setExportLoading(false);
		}
	};

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ExportingModal
				open={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				exportingLoading={exportLoading}
				exportCount={exportCount}
			/>

			<ContactsImportModal
				open={importContactsModal}
				onClose={() => setImportContactsModal(false)}
			/>
			<NewTicketModal
				initialContact={selectedContact}
				modalOpen={newTicketModalOpen}
				onClose={() => setNewTicketModalOpen(false)}
			/>
			<ContactModal
				open={contactModalOpen}
				onClose={handleCloseContactModal}
				aria-labelledby="form-dialog-title"
				contactId={selectedContactId}
			/>
			<ConfirmationModal
				title={
					deletingContact
						? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
						: i18n.t("contacts.confirmationModal.deleteSelectedTitle")
				}
				open={confirmOpen}
				onClose={setConfirmOpen}
				onConfirm={() =>
					deletingContact
						? handleDeleteContact(deletingContact.id)
						: handleDeleteSelected(deletingSelectedIds)
				}
			>
				{deletingContact
					? i18n.t("contacts.confirmationModal.deleteMessage")
					: i18n.t("contacts.confirmationModal.deleteSelectedMessage", {
							count: deletingSelectedIds?.length ?? 0,
					  })}
			</ConfirmationModal>
			<ConfirmationModal
				title={blockingContact?.isBlocked ? "Desbloquear contato" : "Bloquear contato"}
				open={Boolean(blockingContact)}
				onClose={() => setBlockingContact(null)}
				onConfirm={() => blockingContact && handleToggleBlock(blockingContact)}
			>
				{blockingContact?.isBlocked
					? `Deseja desbloquear ${blockingContact?.name || "este contato"} no WhatsApp? O contato voltará a poder enviar mensagens.`
					: `Deseja bloquear ${blockingContact?.name || "este contato"} no WhatsApp? O contato não conseguirá mais enviar mensagens.`}
			</ConfirmationModal>

			<AppPageTitle
				title={i18n.t("contacts.title")}
				subtitle={i18n.t("contacts.subtitle")}
			/>

			<div className="card">
				<div className="card-header">
					<h6 className="card-title m-0">{i18n.t("contacts.listTitle")}</h6>
					<button
						type="button"
						className="btn btn-sm shrink-0 bg-primary text-white"
						onClick={handleOpenContactModal}
					>
						<IconPlus />
						{i18n.t("contacts.buttons.add")}
					</button>
				</div>

				<div className="card-header flex-wrap gap-3">
					<div className="flex w-full min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center md:space-y-0">
						<div className="relative w-full md:max-w-sm">
							<input
								type="search"
								className="form-input form-input-sm w-full ps-9"
								placeholder={i18n.t("contacts.searchPlaceholder")}
								value={searchParam}
								onChange={handleSearch}
							/>
							<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
								<IconSearch />
							</div>
						</div>
						<div className="w-full md:max-w-xs">
							<TagsFilter variant="tailwick" onFiltered={handleSelectedTags} />
						</div>
						<button
							type="button"
							className={`btn btn-sm shrink-0 border ${
								onlyBlocked
									? "border-danger bg-danger/10 text-danger"
									: "border-default-200 bg-transparent text-default-600 hover:border-danger/20 hover:bg-danger/10 hover:text-danger dark:border-default-600 dark:text-default-300"
							}`}
							aria-pressed={onlyBlocked}
							onClick={() => setOnlyBlocked((v) => !v)}
						>
							<IconBan />
							<span className="ms-1">Bloqueados</span>
						</button>
					</div>

					<div className="flex flex-wrap items-center gap-2">
						{selectedCount > 0 && (
							<>
								<span className="text-sm text-default-500 dark:text-default-400">
									{i18n.t("contacts.selectedCount", { count: selectedCount })}
								</span>
								<button
									type="button"
									className="btn btn-sm bg-danger/10 text-danger hover:bg-danger/20 dark:bg-danger/15 dark:hover:bg-danger/25"
									onClick={confirmDeleteSelected}
								>
									<IconTrash />
									<span className="ms-1">{i18n.t("contacts.buttons.deleteSelected")}</span>
								</button>
							</>
						)}
						<button
							type="button"
							className="btn btn-sm shrink-0 border border-dashed border-primary bg-transparent text-primary hover:bg-primary/10"
							onClick={() => setImportContactsModal(true)}
						>
							<IconUpload className="me-1" />
							{i18n.t("contacts.buttons.import")}
						</button>
						<button
							type="button"
							className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
							onClick={exportContacts}
						>
							<IconDownload className="me-1" />
							{i18n.t("contacts.buttons.export")}
						</button>
					</div>
				</div>

				<div className="flex min-h-0 flex-1 flex-col">
					{loading && contacts.length === 0 ? (
						<div className="overflow-x-auto px-2 pt-2">
							<table className="min-w-full">
								<tbody>
									<ContactsTableSkeleton />
								</tbody>
							</table>
						</div>
					) : !loading && contacts.length === 0 ? (
						<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
							<p className="m-0 font-semibold text-default-800 dark:text-default-100">
								{i18n.t("contacts.empty")}
							</p>
							{(searchParam || selectedTags.length > 0) && (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("contacts.emptyHint")}
								</p>
							)}
						</div>
					) : (
						<div className="flex min-h-0 flex-1 flex-col">
							<div className="app-table-scroll">
								<div className="inline-block min-w-full align-middle">
									<div className="overflow-hidden">
										<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
											<thead className="bg-default-150 dark:bg-default-800/60">
												<tr className="whitespace-nowrap text-sm font-normal text-default-700 dark:text-default-300">
													<th scope="col" className="ps-4 text-start">
														<input
															ref={selectAllRef}
															type="checkbox"
															className="form-checkbox"
															checked={allVisibleSelected}
															onChange={toggleSelectAll}
															aria-label={i18n.t("contacts.selectAll")}
															disabled={visibleIds.length === 0 || loading}
														/>
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.name")}
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.tags")}
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.whatsapp")}
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.company")}
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.email")}
													</th>
													<th scope="col" className="px-3.5 py-3 text-start">
														{i18n.t("contacts.table.actions")}
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-default-200 dark:divide-default-700">
												{contacts.map((contact) => (
													<tr
														key={contact.id}
														className="whitespace-nowrap text-sm font-normal text-default-800 dark:text-default-200"
													>
														<td className="py-3 ps-4">
															<input
																type="checkbox"
																className="form-checkbox"
																checked={selectedIds.has(contact.id)}
																onChange={() => toggleSelectOne(contact.id)}
																aria-label={i18n.t("contacts.selectRow", {
																	name: contact.name || contact.number,
																})}
															/>
														</td>
														<td className="flex items-center gap-3 px-3.5 py-3">
															<ContactAvatar contact={contact} />
															<div className="min-w-0">
																<h6 className="mb-0 flex items-center gap-2 font-semibold text-default-800 dark:text-default-100">
																	{contact.name || "—"}
																	{contact.isBlocked && (
																		<span className="inline-flex items-center gap-1 rounded bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
																			<IconBan />
																			Bloqueado
																		</span>
																	)}
																</h6>
																{contact.email && (
																	<p className="m-0 text-default-500">
																		{contact.email}
																	</p>
																)}
															</div>
														</td>
														<td className="px-3.5 py-3">
															<ContactTagsCell tags={contact.tags} />
														</td>
															{(user.userSeeNumbers || user.profile === "admin") ? (
														<td className="px-3.5 py-3 text-default-800 dark:text-default-200">
															{contact.number || "—"}
														</td>
															): <td className="px-3.5 py-3 text-default-800 dark:text-default-200">
															{"Você não tem permissão para visualizar este número."}
														</td>}
														<td className="px-3.5 py-3 text-default-800 dark:text-default-200">
															{contact.company || "—"}
														</td>
														<td className="px-3.5 py-3 text-default-800 dark:text-default-200">
															{contact.email || "—"}
														</td>
														<td className="px-3.5 py-3">
															<Can
																role={user.profile}
																perform="contacts-page:deleteContact"
																yes={() => (
																	<ContactRowActions
																		onChat={() => handleGoToConversation(contact)}
																		onEdit={() => hadleEditContact(contact.id)}
																		onDelete={() => confirmDeleteOne(contact)}
																		canDelete
																		onToggleBlock={contact.isGroup ? null : () => setBlockingContact(contact)}
																		isBlocked={contact.isBlocked}
																	/>
																)}
																no={() => (
																	<ContactRowActions
																		onChat={() => handleGoToConversation(contact)}
																		onEdit={() => hadleEditContact(contact.id)}
																		onDelete={() => {}}
																		canDelete={false}
																		onToggleBlock={contact.isGroup ? null : () => setBlockingContact(contact)}
																		isBlocked={contact.isBlocked}
																	/>
																)}
															/>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>

							{total > 0 && (
								<div className="card-footer">
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										{i18n.t("contacts.pagination.showing", {
											start: rangeStart,
											end: rangeEnd,
											total,
										})}
									</p>
									<nav
										className="flex flex-wrap items-center gap-2"
										aria-label={i18n.t("contacts.pagination.label")}
									>
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={pageNumber <= 1 || loading}
											onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
										>
											<IconChevronLeft className="size-4 me-1 shrink-0" />
											{i18n.t("contacts.pagination.previous")}
										</button>
										{pageNumbers().map((p) => (
											<button
												key={p}
												type="button"
												className={`btn size-7.5 shrink-0 border ${
													p === pageNumber
														? "border-primary bg-primary text-white"
														: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
												}`}
												disabled={loading}
												onClick={() => setPageNumber(p)}
											>
												{p}
											</button>
										))}
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={!hasMore || loading || pageNumber >= totalPages}
											onClick={() => setPageNumber((p) => p + 1)}
										>
											{i18n.t("contacts.pagination.next")}
											<IconChevronRight className="size-4 ms-1 shrink-0" />
										</button>
									</nav>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default Contacts;
